class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // Listen for cart refresh events from fragrance carousel
    document.addEventListener('cart:refresh', this.handleCartRefresh.bind(this));
    document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      // Remove any existing listeners to prevent duplicates
      const newCartLink = cartLink.cloneNode(true);
      cartLink.parentNode.replaceChild(newCartLink, cartLink);

      newCartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(newCartLink);
      });
      newCartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(newCartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // *** CRITICAL FIX ***
    // After updating the cart icon bubble's HTML, we must re-attach its
    // event listeners and accessibility attributes.
    if (parsedState.sections['cart-icon-bubble']) {
      this.setHeaderCartIconAccessibility();
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // Handle cart refresh event from fragrance carousel
  handleCartRefresh(event) {
    // Refresh the cart drawer contents
    this.refreshCart();
    
    // Open the cart drawer
    this.open();
  }

  // Handle cart update event from fragrance carousel
  handleCartUpdate(event) {
    // Update cart count bubble
    this.updateCartCount(event.detail.cart.item_count);
    
    // Refresh the cart drawer contents
    this.refreshCart();
  }

  // Refresh cart drawer contents
  refreshCart() {
    // Use window.location.origin to ensure we're using the correct domain
    const cartUrl = `${window.location.origin}/cart?section_id=cart-drawer`;
    
    fetch(cartUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('.drawer__inner');
        const destinationQty = document.querySelector('.drawer__inner');
        
        if (sourceQty && destinationQty) {
          destinationQty.innerHTML = sourceQty.innerHTML;
          
          // Remove empty class if cart has items
          if (html.querySelector('.cart-item')) {
            destinationQty.classList.remove('is-empty');
          }
        }
        
        // Also update the cart icon bubble
        const sourceBubble = html.querySelector('#cart-icon-bubble');
        const destinationBubble = document.querySelector('#cart-icon-bubble');
        
        if (sourceBubble && destinationBubble) {
          // Preserve the parent element structure to prevent layout shifts
          const parent = destinationBubble.parentNode;
          const nextSibling = destinationBubble.nextSibling;
          
          // Replace the inner HTML, not the entire element
          destinationBubble.innerHTML = sourceBubble.innerHTML;
          
          // Ensure the cart icon is visible
          destinationBubble.style.display = '';
          destinationBubble.classList.remove('hidden');
          
          // *** CRITICAL FIX ***
          // After replacing the icon's HTML, we must re-bind its functionality.
          this.setHeaderCartIconAccessibility();
        }
      })
      .catch((e) => {
        console.error('Error refreshing cart:', e);
      });
  }

  // Update cart count bubble
  updateCartCount(count) {
    const cartCountElements = document.querySelectorAll('.cart-count, .cart-item-count, .cart-count-bubble');
    cartCountElements.forEach(element => {
      element.textContent = count;
      
      // *** CRITICAL FIX ***
      // Ensure the cart icon is visible when there are items in the cart
      if (count > 0) {
        const cartIconBubble = element.closest('#cart-icon-bubble');
        if (cartIconBubble) {
          cartIconBubble.style.display = '';
          cartIconBubble.classList.remove('hidden');
        }
      }
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);